import { Badge } from "@workspace/web-ui/components/badge";
import { Button } from "@workspace/web-ui/components/button";
import { Separator } from "@workspace/web-ui/components/separator";
import React, { useState } from "react";
import { MedicationRefillRequestModal } from "~/features/dashboard/components/medication-request";
import { PastMedication } from "~/features/dashboard/pages/medicals/types";
import { formatMedicationDate } from "@workspace/lib/date";
import { Paginator } from "./pagination";

interface Medication {
  medicalItems: PastMedication[];
  program?: string;
  isLoading?: boolean;
  totalCount?: number;
}

const ITEMS_PER_PAGE = 5; // Display 5 items per page

export const getStatusConfig = (item: PastMedication) => {
  let status = item.status

  const statusMap = {
    "active": { color: "bg-[#E6FAF2] text-[#00C781] hover:bg-[#E6FAF2]", text: "Active", status: "active" },
    "new": { color: "bg-[#FEF2FF] text-[#942BA9]", text: "New", status: "new" },
    "refill_due": { color: "bg-[#FEEBEB] text-[#F53535] hover:bg-[#FEEBEB]", text: "Out of supply", status: "refill_due" },
    "requested": { color: "bg-[#FFF6E7] text-[#FFAB1F] hover:bg-[#FFF6E7]", text: "Refill requested", status: "requested" },
    "rejected": { color: "bg-[#0404040D] text-[#464B55] hover:bg-[#0404040D]", text: "Refill rejected", status: "rejected" },
  };

  return statusMap[status as keyof typeof statusMap];
};

export function PastMedications({ medicalItems, totalCount }: Medication) {
  const [currentPage, setCurrentPage] = useState(1);
  const [requestRefill, setRequestRefill] = React.useState(false);
  const [reqRefillPrescriptionId, setReqRefillPrescriptionId] = React.useState("");
  const [reqRefillMedicationId, setReqRefillMedicationId] = React.useState("");

  // Calculate total pages
  const totalPages = Math.ceil(medicalItems.length / ITEMS_PER_PAGE);

  // Get data for the current page
  const paginatedData = medicalItems.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const renderButtons = (item: PastMedication) => {
    const statusConfig = getStatusConfig(item)

    return (
      <div key={item.medication.medication_id} className="flex flex-col border border-[#0404041A] shadow-sm rounded-[8px] mb-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between w-full py-3 sm:py-[14px] px-3 sm:px-[20px] gap-3 sm:gap-0">
          <div className="flex flex-col gap-1 w-full">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="font-medium text-base sm:text-lg text-[#2D2F35]">{item.medication.name}</h3>
              <span className="text-xs sm:text-sm">({item.medication.duration} supply)</span>
              <Badge className={`${statusConfig.color} text-xs font-medium`}><span className="mr-1 ">{"\u2B24"}</span> {statusConfig.text}</Badge>
            </div>

            <div className="flex">
              <h6 className="text-xs sm:text-sm font-normal text-[#202020CC] text-left">
                Prescribed: {formatMedicationDate(item.prescribed_on || "")} <span className="mx-1 text-[#C9C9CC]">{"\u2022"}</span>
                {item.medication.dosage} <span className="mx-1 text-[#C9C9CC]">{"\u2022"}</span> {item.medication.duration}
              </h6>
            </div>
          </div>

          <Button
            variant="default"
            // disabled={true}
            // disabled={item.status === "rejected" || item.status === "requested"}
            onClick={() => {
              setReqRefillMedicationId(item.medication_id)
              setReqRefillPrescriptionId(item.prescription_id)
              setRequestRefill(true)
            }}
            className="sm:ml-4 rounded-lg font-semibold w-full sm:w-auto text-sm mt-2 sm:mt-0"
          >
            Request refill
          </Button>
        </div>

        {/* rejected message */}
        {item.status === "rejected" && (
          <div className="flex items-center gap-2 sm:gap-4 w-full bg-[#0404040D] py-3 sm:py-[12px] px-3 sm:px-[20px] transition-colors rounded-b-[8px]">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M8.00008 0.666504C3.94999 0.666504 0.666748 3.94975 0.666748 7.99984C0.666748 12.0499 3.94999 15.3332 8.00008 15.3332C12.0502 15.3332 15.3334 12.0499 15.3334 7.99984C15.3334 3.94975 12.0502 0.666504 8.00008 0.666504ZM8.00008 4.6665C7.63189 4.6665 7.33341 4.96498 7.33341 5.33317C7.33341 5.70136 7.63189 5.99984 8.00008 5.99984H8.00675C8.37494 5.99984 8.67341 5.70136 8.67341 5.33317C8.67341 4.96498 8.37494 4.6665 8.00675 4.6665H8.00008ZM8.66675 7.99984C8.66675 7.63165 8.36827 7.33317 8.00008 7.33317C7.63189 7.33317 7.33341 7.63165 7.33341 7.99984V10.6665C7.33341 11.0347 7.63189 11.3332 8.00008 11.3332C8.36827 11.3332 8.66675 11.0347 8.66675 10.6665V7.99984Z" fill="#4272DD" />
            </svg>
            <p className="text-xs sm:text-sm font-normal text-[#202020CC] text-left">
              {"Your request for this refill was rejected, please contact support"}
            </p>
          </div>
        )}

        {/* requested message */}
        {item.status === "requested" && (
          <div className="flex items-center gap-2 sm:gap-4 w-full bg-[#FFF5E6] py-3 sm:py-[12px] px-3 sm:px-[20px] transition-colors rounded-b-[8px]">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M8.00033 0.666504C3.95024 0.666504 0.666992 3.94975 0.666992 7.99984C0.666992 12.0499 3.95024 15.3332 8.00033 15.3332C12.0504 15.3332 15.3337 12.0499 15.3337 7.99984C15.3337 3.94975 12.0504 0.666504 8.00033 0.666504ZM8.00033 4.6665C7.63213 4.6665 7.33366 4.96498 7.33366 5.33317C7.33366 5.70136 7.63213 5.99984 8.00033 5.99984H8.00699C8.37518 5.99984 8.67366 5.70136 8.67366 5.33317C8.67366 4.96498 8.37518 4.6665 8.00699 4.6665H8.00033ZM8.66699 7.99984C8.66699 7.63165 8.36852 7.33317 8.00033 7.33317C7.63213 7.33317 7.33366 7.63165 7.33366 7.99984V10.6665C7.33366 11.0347 7.63213 11.3332 8.00033 11.3332C8.36852 11.3332 8.66699 11.0347 8.66699 10.6665V7.99984Z" fill="#FFA726" />
            </svg>
            <p className="text-xs sm:text-sm font-normal text-[#202020CC] text-left">
              {"You requested for a refill for this medicine. A doctor will review shortly"}
            </p>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="border rounded-xl shadow-sm bg-white w-full text-center mb-4 ">
      <h3 className="flex items-center text-base sm:text-lg font-medium text-left p-4 sm:p-5 text-[#1A202C]">Past medications <Badge className="ml-2 size-4 rounded-full bg-[#4272DD] border border-[#0404041A] p-2 justify-center items-center text-center text-white text-xs">
        {totalCount || 0}
      </Badge></h3>
      <Separator />
      <div className="space-y-4 bg-card rounded-[8px] p-3 sm:p-4">
        {paginatedData.map((med) => renderButtons(med))}
      </div>

      <Paginator items={medicalItems} totalPages={totalPages} currentPage={currentPage} onPageChange={setCurrentPage} itemsPerPage={ITEMS_PER_PAGE} className="border-t" />

      {requestRefill && reqRefillPrescriptionId && reqRefillMedicationId && <MedicationRefillRequestModal
        prescription_id={reqRefillPrescriptionId}
        medication_id={reqRefillMedicationId}
        open={requestRefill}
        onClose={() => setRequestRefill(false)}
      />}
    </div>
  )
}
